import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function HounslowCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Hounslow Pothole Claims | Hounslow Highways PFI & 4,681 Repairs | Fixtyer"
        description="Hounslow runs a 25-year Hounslow Highways PFI yet earns AMBER overall despite GREEN condition and spend scorecards. A-road amber condition hit 27.4%, and 76% of the 435km network is residential. See what this means for your pothole claim."
        canonical="/councils/hounslow"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Hounslow', url: '/councils/hounslow' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Hounslow has GREEN condition and spend scorecards — can I still claim?',
            answer: 'Yes. The DfT Condition and Spend ratings are GREEN, but your claim turns on the specific defect that damaged your vehicle, not borough-wide averages. Hounslow\'s own data shows A-road amber condition at a five-year high of 27.4%, 10% of U-roads still in RED condition, and 35% of maintenance spend was reactive as recently as 2024/25. Section 58 depends on whether your road was reasonably inspected and the defect repaired in time.',
          },
          {
            question: 'What does the 25-year Hounslow Highways PFI mean for my claim?',
            answer: 'Hounslow outsources highways maintenance to Vinci UK/Ringway under a 25-year private finance initiative, trading as Hounslow Highways. Capital renewal is PFI-funded, while reactive pothole repairs come from a separate PFI revenue budget. The council cites ISO 55001 accreditation and annual independent surveys — a documented system it can invoke under Section 58. But liability still rests with Hounslow Council as highway authority, and defects identified only through "punctual residents\' reports" or scheduled inspections can still leave gaps.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 332.6km — 76.5% of Hounslow\'s 434.7km adopted road network. The council carries out Detailed Visual Inspection on carriageways and Footway Network Survey on 100% of U-roads each year. At the last survey, 10% of U-roads were in RED condition — down from 15% in 2020/21 but still roughly 33km of residential streets. Prior reports of the same defect matter because immediate defects are also identified through residents\' reports alongside scheduled inspections.',
          },
          {
            question: 'Why is best practice AMBER when Hounslow has ISO 55001 accreditation?',
            answer: 'Hounslow Highways holds ISO 55001 certification and publishes spending figures showing capital spend nearly four times the DfT allocation in 2025/26. Yet the DfT Best Practice scorecard is AMBER — the same split seen at other PFI councils where a long-term contract bundles maintenance into aggregated programmes. The council\'s own transparency page also has condition and pothole data tables present in the published report source but not currently displayed in the live accordion sections, which weakens public-facing transparency even where the underlying data exists.',
          },
          {
            question: 'Hounslow fills fewer than 1,100 potholes a year — does that mean the roads are well maintained?',
            answer: 'Hounslow filled 1,035 potholes in 2024/25 and plans for 937 in 2025/26 based on the five-year average. That is far fewer than larger counties, but on a compact 434.7km network it still means roughly three pothole repairs every day. The council also notes 75% of carriageway revenue spend goes on pothole repairs, and reactive maintenance in 2024/25 accounted for 35% of total maintenance spend — evidence that defects still form and require intervention between planned treatments.',
          },
          {
            question: 'Does Hounslow inspect roads often enough for Section 58?',
            answer: 'Hounslow surveys 100% of U-roads yearly via walked visual inspection and mechanised SCRIM and SCANNER equipment on the project network. Immediate defects are identified through scheduled road safety inspections "in accordance to the roads\' hierarchy" and ad-hoc public reports. For Section 58, the question is whether your specific road was inspected within a reasonable interval before your incident — and whether any prior report of the same defect gave the council actual notice before the scheduled inspection would have found it.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Hounslow Council"
        councilSlug="hounslow"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/hounslow"
        headline="Hounslow Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Hounslow</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Hounslow Highways PFI: Green Scorecards, Amber Oversight
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Hounslow Council hands its entire 434.7km adopted network to a <strong className="text-white">25-year PFI
            contract</strong> with Vinci UK/Ringway — Hounslow Highways. The DfT awards
            <strong className="text-emerald-400"> GREEN</strong> for both condition and spend, yet the overall rating is
            <strong className="text-amber-400"> AMBER</strong> and best practice is <strong className="text-amber-400">AMBER</strong> too.
            A-road amber condition has climbed to <strong className="text-rose-400">27.4%</strong>, capital spend is
            projected at nearly <strong className="text-emerald-400">four times</strong> the DfT allocation, and
            <strong className="text-rose-400"> 76% of the borough's roads are residential U-roads</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">25 years</div>
            <div className="text-white font-medium mt-2">Hounslow Highways PFI contract</div>
            <div className="text-brand-300 text-sm mt-1">
              Vinci UK/Ringway manages and maintains every adopted A, B, C and U road in the borough — 434.7km of
              carriageway, 796km of footways and 45 structures — under a single long-term highways PFI.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The PFI */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Hounslow Highways PFI
          </h2>
          <p className="text-surface-600 mb-8">
            A borough-wide outsourcing model — similar in scale to Sheffield's Streets Ahead — with ISO 55001
            accreditation and independent annual surveys
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We have a 25-year private finance initiative (PFI) for highways maintenance with Vinci UK/Ringway. This company is known locally as Hounslow Highways. They are responsible for managing and maintaining our adopted highway network."
              source="Hounslow Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The process in use to manage highway assets in Hounslow have been devised at the start of the PFI Contract to align with best practice. The quality of our approach and data is monitored each year through a stringent regime of internal and external audits."
              source="Hounslow Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">32.5km</div>
              <div className="text-sm text-surface-600">A roads (7.5%)</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-2xl font-bold text-brand-900">69.6km</div>
              <div className="text-sm text-surface-600">B and C roads (16%)</div>
            </div>
            <div className="bg-amber-50 rounded-xl p-5 border border-amber-200 text-center">
              <div className="text-2xl font-bold text-amber-700">332.6km</div>
              <div className="text-sm text-amber-800">U roads (76.5%)</div>
            </div>
            <div className="bg-brand-950 text-white rounded-xl p-5 text-center">
              <div className="text-2xl font-bold">434.7km</div>
              <div className="text-sm text-brand-300">Total adopted roads</div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">What The PFI Means For Claims</h3>
            <p className="text-brand-200 mb-4">
              Hounslow Council remains the highway authority. Hounslow Highways is the contractor. Under Section 58,
              the council can point to a documented, ISO 55001-aligned maintenance system with yearly independent
              surveys and a formal Asset Optioneering Model. That is a stronger defence than a council with no published
              inspection regime.
            </p>
            <p className="text-white font-medium">
              But a PFI does not eliminate liability. If a defect was reported and not repaired, or formed between
              inspections on a road the hierarchy did not prioritise, the contract structure is irrelevant — the
              council still had a duty to keep the highway reasonably safe.
            </p>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Hounslow's own transparency report — GREEN at headline level, with
            creeping amber on A-roads and persistent RED on residential streets
          </p>

          {/* A-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">A-roads (32.5km — 7.5% of network): amber rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.8%</td>
                    <td className="px-4 py-2 text-center">21.7%</td>
                    <td className="px-4 py-2 text-center">75.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.1%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">73.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">24.4%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.2%</td>
                    <td className="px-4 py-2 text-center">23.8%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.8%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">27.4%</td>
                    <td className="px-4 py-2 text-center font-bold">68.8%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              A-road amber condition has climbed from 21.7% to 27.4% — a five-year high — while good-condition A-roads
              have fallen from 75.5% to 68.8%. RED A-roads are also up from 2.8% to 3.8%.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (69.6km — 16% of network): broadly stable</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">17%</td>
                    <td className="px-4 py-2 text-center">80.4%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2.7%</td>
                    <td className="px-4 py-2 text-center">19.2%</td>
                    <td className="px-4 py-2 text-center">78.1%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.8%</td>
                    <td className="px-4 py-2 text-center">17.8%</td>
                    <td className="px-4 py-2 text-center">79.4%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-medium text-rose-600">3.7%</td>
                    <td className="px-4 py-2 text-center font-medium text-amber-600">22.7%</td>
                    <td className="px-4 py-2 text-center">73.6%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">2.1%</td>
                    <td className="px-4 py-2 text-center font-bold">19%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">78.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B and C roads recovered in 2024 after a 2023 spike, with RED condition back down to 2.1%. But the 2023
              deterioration — 3.7% RED and 22.7% amber — shows the network is not immune to localised decline even
              under a PFI.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">U-roads (332.6km — 76.5% of network): RED persists</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">9%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">10%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">10%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium mb-4">
              U-road RED condition improved from 15% to 10% — but 10% of 332.6km is still roughly 33km of residential
              streets in the worst category. That is where most Hounslow pothole claims happen.
            </p>
            <QuoteCard
              quote="We carry out Detailed Visual Inspection on carriageways and Footway Network Survey on 100% of the U roads. We are ISO:550001 accredited since 2017."
              source="Hounslow Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend — PFI-Funded Capital, Reactive Revenue
          </h2>
          <p className="text-surface-600 mb-8">
            Hounslow's spending table separates PFI-funded capital renewal from reactive defect repairs — and the
            preventative share is climbing
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100 bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">£757k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£2,951k</td>
                  <td className="px-4 py-3 text-center">£718k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">84%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£233k</td>
                  <td className="px-4 py-3 text-center">£832k</td>
                  <td className="px-4 py-3 text-center">£695k</td>
                  <td className="px-4 py-3 text-center">65%</td>
                  <td className="px-4 py-3 text-center font-medium text-amber-600">35%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£233k</td>
                  <td className="px-4 py-3 text-center">£1,090k</td>
                  <td className="px-4 py-3 text-center">£700k</td>
                  <td className="px-4 py-3 text-center">62%</td>
                  <td className="px-4 py-3 text-center">38%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£685k</td>
                  <td className="px-4 py-3 text-center">£595k</td>
                  <td className="px-4 py-3 text-center">54%</td>
                  <td className="px-4 py-3 text-center font-medium text-rose-600">46%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£3,145k</td>
                  <td className="px-4 py-3 text-center">£551k</td>
                  <td className="px-4 py-3 text-center">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£1,803k</td>
                  <td className="px-4 py-3 text-center">£535k</td>
                  <td className="px-4 py-3 text-center">77%</td>
                  <td className="px-4 py-3 text-center">23%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Capital Is PFI-Funded</h3>
              <p className="text-emerald-800 text-sm mb-3">
                Hounslow's capital spend includes annual carriageway and footway renewal funded by the Hounslow PFI
                project, plus DfT surfacing grants and council pledge allocations. In 2025/26, projected capital spend
                of £2.951m is nearly four times the £757k DfT allocation.
              </p>
              <p className="text-emerald-800 text-sm">
                The council plans 44,313 sq.m of carriageway resurfacing in 2025/26 — 23,929 sq.m from the PFI Annual
                Major Maintenance Programme plus DfT and traffic-scheme funded works.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Revenue Is Reactive-Heavy</h3>
              <QuoteCard
                quote="Of the revenue budget, 65% is spent on footway and 35% is spent on carriageway. Of the carriageway amount, 75% is then spent on pothole repairs."
                source="Hounslow Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The Preventative-Reactive Swing</h3>
            <p className="text-xl font-semibold text-white mb-4">
              In 2024/25, <span className="text-amber-400">35% of maintenance spend was reactive</span>. Hounslow
              projects that falling to 16% in 2025/26 — but reactive spend was as high as 46% in 2022/23. A council
              that spends a third of its budget reacting to defects is a council where potholes are still forming
              between planned treatments.
            </p>
            <p className="text-brand-300 text-sm">
              The above approach to Asset Management has received ISO 55001 certification — but certification
              describes process, not outcomes on your specific road.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            4,681 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Modest absolute numbers on a compact London borough network — but the council still budgets for nearly
            1,000 repairs a year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">1,084</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">955</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-medium text-emerald-600">715</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">892</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">1,035</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,681</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">937 Planned for 2025/26</h3>
              <QuoteCard
                quote="Filling 937 potholes, based on an average of the total number of potholes filled over the last five years."
                source="Hounslow Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">How Defects Are Found</h3>
              <QuoteCard
                quote="Immediate defects are identified through a mixture of punctual residents' reports and scheduled road safety inspections done by highways inspectors in accordance to the roads' hierarchy. These defects are then addressed as part of our reactive maintenance programme."
                source="Hounslow Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Inspection methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspection and Survey Regime
          </h2>
          <p className="text-surface-600 mb-8">
            Annual surveys on 100% of U-roads — but reactive repairs still depend on residents reporting defects promptly
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Visual and Mechanised Surveys</h3>
              <p className="text-surface-600 text-sm mb-3">
                Condition is monitored through Detailed walked visual inspection (DVI) on carriageways and Footway
                Network Survey (FNS) on footways — and mechanised vehicle-mounted SCRIM and SCANNER survey equipment
                looking at longitudinal profile, rutting and surface friction.
              </p>
              <p className="text-surface-600 text-sm">
                The project network is divided into Road Section Lengths and Footway Section Lengths, surveyed each year
                by an independent accredited company. Results feed into the Asset Optioneering Model to prioritise
                intervention.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Residents' Report Dependency</h3>
              <p className="text-amber-800 text-sm mb-3">
                Reactive maintenance spend is made up of smaller localised interventions on sections that have reached
                the intervention level — identified through routine safety inspection and ad-hoc reports from members of
                the public through the customer management system.
              </p>
              <p className="text-amber-800 text-sm">
                The council's own wording — "punctual residents' reports" — acknowledges that public reporting is part
                of the inspection system, not an optional extra. Prior reports of your pothole establish actual notice.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Hounslow has a more rigorous published survey regime than many councils — yearly U-road coverage, ISO
              55001, independent surveys, and a formal Asset Optioneering Model. For Section 58, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road surveyed in the year before your incident — and did the survey catch this defect?</li>
              <li>• Did you or anyone else report the pothole before the damage occurred?</li>
              <li>• What is the road's hierarchy classification — and does that determine inspection frequency?</li>
              <li>• If 75% of carriageway revenue goes on pothole repairs, why was yours not fixed?</li>
              <li>• Was the defect on a section scheduled for 2025/26 resurfacing that had not yet been treated?</li>
            </ul>
            <p className="text-white font-medium">
              A well-documented PFI system raises the bar for your evidence — but prior reports and photos of a
              clearly aged defect can still defeat it.
            </p>
          </div>
        </div>
      </section>

      {/* What GREEN + AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Condition, AMBER-Overall Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Hounslow is not Derbyshire — here is how the scorecard split changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition scorecard — network averages outperform many authorities
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend nearly 4× DfT allocation in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> ISO 55001 accredited asset management since 2017
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% annual U-road survey coverage via DVI and FNS
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED condition improved from 15% to 10% since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 44,313 sq.m of planned resurfacing in 2025/26
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence citing the PFI contract, ISO 55001 and annual surveys.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall and best practice — PFI complexity drags headline ratings
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road amber condition at a five-year high of 27.4%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 10% of U-roads still in RED — roughly 33km of residential streets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 35% reactive maintenance spend in 2024/25 — defects still forming between treatments
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 4,681 pothole repairs in five years — the network still produces defects at scale
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive defects depend partly on "punctual residents' reports" — prior reports are proof of notice
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN condition and spend scorecards plus an ISO-certified PFI contractor, your
              claim lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice before your incident</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — 76.5% of Hounslow is U-roads, where 10% remain in RED condition</li>
              <li>• Whether the road was on the 2025/26 resurfacing programme but not yet treated when you hit the pothole</li>
              <li>• The road hierarchy — inspection frequency varies by classification</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              Hounslow's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Hounslow?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A PFI-backed council demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No PFI contract scrutiny</li>
                <li>• No prior-report search</li>
                <li>• No road-hierarchy argument</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ Hounslow Highways PFI context documented</li>
                <li>✅ U-road RED condition and survey regime argued</li>
                <li>✅ 4,681 five-year pothole repairs cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Hounslow</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=hounslow"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Hounslow Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Hounslow has GREEN condition and spend scorecards — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Condition and Spend ratings are GREEN, but your claim turns on the specific defect that
                damaged your vehicle, not borough-wide averages. Hounslow's own data shows A-road amber condition at a
                five-year high of 27.4%, 10% of U-roads still in RED condition, and 35% of maintenance spend was reactive
                as recently as 2024/25. Section 58 depends on whether your road was reasonably inspected and the defect
                repaired in time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the 25-year Hounslow Highways PFI mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Hounslow outsources highways maintenance to Vinci UK/Ringway under a 25-year private finance initiative,
                trading as Hounslow Highways. Capital renewal is PFI-funded, while reactive pothole repairs come from a
                separate PFI revenue budget. The council cites ISO 55001 accreditation and annual independent surveys — a
                documented system it can invoke under Section 58. But liability still rests with Hounslow Council as
                highway authority, and defects identified only through "punctual residents' reports" or scheduled
                inspections can still leave gaps.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 332.6km — 76.5% of Hounslow's 434.7km adopted road network. The council carries out
                Detailed Visual Inspection on carriageways and Footway Network Survey on 100% of U-roads each year. At
                the last survey, 10% of U-roads were in RED condition — down from 15% in 2020/21 but still roughly 33km
                of residential streets. Prior reports of the same defect matter because immediate defects are also
                identified through residents' reports alongside scheduled inspections.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is best practice AMBER when Hounslow has ISO 55001 accreditation?
              </h3>
              <p className="text-surface-600 text-sm">
                Hounslow Highways holds ISO 55001 certification and publishes spending figures showing capital spend
                nearly four times the DfT allocation in 2025/26. Yet the DfT Best Practice scorecard is AMBER — the same
                split seen at other PFI councils where a long-term contract bundles maintenance into aggregated
                programmes. The council's own transparency page also has condition and pothole data tables present in the
                published report source but not currently displayed in the live accordion sections, which weakens
                public-facing transparency even where the underlying data exists.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Hounslow fills fewer than 1,100 potholes a year — does that mean the roads are well maintained?
              </h3>
              <p className="text-surface-600 text-sm">
                Hounslow filled 1,035 potholes in 2024/25 and plans for 937 in 2025/26 based on the five-year average.
                That is far fewer than larger counties, but on a compact 434.7km network it still means roughly three
                pothole repairs every day. The council also notes 75% of carriageway revenue spend goes on pothole
                repairs, and reactive maintenance in 2024/25 accounted for 35% of total maintenance spend — evidence that
                defects still form and require intervention between planned treatments.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Hounslow inspect roads often enough for Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                Hounslow surveys 100% of U-roads yearly via walked visual inspection and mechanised SCRIM and SCANNER
                equipment on the project network. Immediate defects are identified through scheduled road safety
                inspections "in accordance to the roads' hierarchy" and ad-hoc public reports. For Section 58, the
                question is whether your specific road was inspected within a reasonable interval before your incident —
                and whether any prior report of the same defect gave the council actual notice before the scheduled
                inspection would have found it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.hounslow.gov.uk/roads-streets/highway-maintenance-works-1"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Hounslow Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
